import { useCallback } from 'react';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { buildAdditionalCategoryAdditionOperations } from './additionalCategoryPlan';
import { notifyParameterAddition } from './feedback';
import { runCoalescedParameterAddition } from './operationIdentity';
import { type ParameterAdditionOptions, type ParameterAdditionResult } from './models';
import { runAdditionalCategoryFamilyAddition } from './additionalCategoryFamilyAddition';
import { runAdditionalCategoryProjectAddition } from './additionalCategoryProjectAddition';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
export function useAdditionalCategoryParameterAddition(
  core: ParameterAdditionCore,
  mutations: ParameterMutationActions,
) {
  const {
    addAdditionalCategoryParametersInFlightRef,
    additionalCategoryBatchOperation,
    currentProjectUndoKey,
    didProjectChange,
    setAdditionalCategoryParameterUndoPlan,
    setIsAdding,
    setParameterProgress,
    showError,
    showInfo,
    showSuccess,
    showWarning,
    startParameterProgress,
  } = core;
  const { addAsProjectParameters, addToObjectTypeFamilies } = mutations;

  const addAdditionalCategoryParameters = useCallback(
    async (
      options: ParameterAdditionOptions = {},
    ): Promise<ParameterAdditionResult> => {
      return runCoalescedParameterAddition(
        addAdditionalCategoryParametersInFlightRef,
        async () => {
          const liveConfiguration = useConfigurationStore.getState();
          const frozenAdditionalCategoriesConfig =
            liveConfiguration.additionalCategoriesConfiguration;
          const frozenMenuConfig = liveConfiguration.menuConfiguration;
          const operations = buildAdditionalCategoryAdditionOperations(
            frozenAdditionalCategoriesConfig,
          );

          if (operations.length === 0) {
            return { success: true, addedCount: 0 };
          }

          const targetParameterNames = sanitizeRevitParameterNames(
            operations.flatMap((operation) => operation.parameterNames),
          );

          const requestedProjectKey = currentProjectUndoKey;
          if (!requestedProjectKey) {
            const error = 'Active Revit project identity is not available yet. Recheck this step before continuing.';
            notifyParameterAddition(options, showWarning, error);
            return { success: false, addedCount: 0, error };
          }

          setIsAdding(true);
          startParameterProgress({
            title: 'Adding category parameters',
            message: 'Revit is adding category parameters',
            detail: (frozenAdditionalCategoriesConfig.parameterDestination ?? 'project') ===
                'family'
              ? 'Editable VALVE, SUPPORT, and INSTRUMENT families are updated.'
              : 'Selected project categories are updated.',
          });
          setAdditionalCategoryParameterUndoPlan(null);
          try {
            if ((frozenAdditionalCategoriesConfig.parameterDestination ?? 'project') === 'project') {
              return runAdditionalCategoryProjectAddition(
                {
                  addAsProjectParameters,
                  additionalCategoriesConfig: frozenAdditionalCategoriesConfig,
                  didProjectChange,
                  setAdditionalCategoryParameterUndoPlan,
                  showInfo,
                  showSuccess,
                  showWarning,
                },
                operations,
                targetParameterNames,
                requestedProjectKey,
                options,
              );
            }

            return runAdditionalCategoryFamilyAddition(
              {
                addToObjectTypeFamilies,
                additionalCategoriesConfig: frozenAdditionalCategoriesConfig,
                additionalCategoryBatchOperation,
                didProjectChange,
                menuConfig: frozenMenuConfig,
                setAdditionalCategoryParameterUndoPlan,
                setParameterProgress,
                showInfo,
                showWarning,
              },
              requestedProjectKey,
              options,
            );
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            notifyParameterAddition(
              options,
              showError,
              `Failed to queue additional-category parameters: ${message}`,
            );
            return {
              success: false,
              addedCount: 0,
              error: message,
            };
          } finally {
            setIsAdding(false);
          }
        },
      );
    },
    [
      addToObjectTypeFamilies,
      addAsProjectParameters,
      additionalCategoryBatchOperation,
      currentProjectUndoKey,
      didProjectChange,
      addAdditionalCategoryParametersInFlightRef,
      setIsAdding,
      setParameterProgress,
      setAdditionalCategoryParameterUndoPlan,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      startParameterProgress,
    ],
  );

  return addAdditionalCategoryParameters;
}
